import dayjs from 'dayjs';
import { FC, ReactNode, useState } from 'react';
import {
  CurrencyTypeWithDiscount,
  REWARD_CHANNEL_LABELS,
  RewardChannel
} from '../../constants/rewards.const';
import { cn } from '../../lib/utils';
import { UIReward } from '../../types/rewards';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader } from '../ui/dialog';

interface RewardCardProps extends UIReward {
  merchantName?: string;
  multiplier?: number;
  showTnc?: boolean;
  availableMRP?: number;
  /** background color of reward card - as per current tier color */
  color?: string;
}

// SVG Icon Components
const CashbackIcon = ({
  disabled,
  color
}: {
  disabled: boolean;
  color?: string;
}) => (
  <svg
    className={disabled ? 'text-[#C4C4C4]' : color ? '' : 'text-primary'}
    style={!disabled && color ? { color: color } : undefined}
    width="28"
    height="28"
    viewBox="0 0 28 28"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="2.32812"
      y="6.99805"
      width="23.3333"
      height="18.6667"
      rx="5.83333"
      stroke="currentColor"
      strokeWidth="1.75"
    />
    <path
      d="M22.1615 7.58203V7.58203C22.1615 4.87177 19.6739 2.84418 17.0193 3.39071L6.98515 5.45657C4.27376 6.01479 2.32813 8.40181 2.32813 11.1701L2.32812 15.1654"
      stroke="currentColor"
      strokeWidth="1.75"
    />
    <path
      d="M6.99219 20.4154H13.9922"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M17.4922 16.3327C17.4922 14.7219 18.798 13.416 20.4089 13.416H25.6589V19.2493H20.4089C18.798 19.2493 17.4922 17.9435 17.4922 16.3327V16.3327Z"
      stroke="currentColor"
      strokeWidth="1.75"
    />
    <path
      d="M20.4141 16.332H20.6474"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DiscountIcon = ({
  disabled,
  color
}: {
  disabled: boolean;
  color?: string;
}) => (
  <svg
    className={disabled ? 'text-[#C4C4C4]' : color ? '' : 'text-primary'}
    style={!disabled && color ? { color: color } : undefined}
    width="29"
    height="29"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.76434 21.3246L2.84771 21.7243L3.76434 21.3246ZM21.3103 28.2302L21.7095 29.147L21.3103 28.2302ZM28.2197 21.3242L27.3031 20.9245L28.2197 21.3242ZM28.2178 10.6709L27.3013 11.071L28.2178 10.6709ZM11.112 28.4118L10.7458 29.3424L11.112 28.4118ZM21.3176 3.77249L20.9178 4.6891L21.3176 3.77249ZM28.404 11.1198L27.4734 11.4859L28.404 11.1198ZM20.8719 28.4118L20.5057 27.4812L20.8719 28.4118ZM11.112 3.58769L11.4781 4.51825L11.112 3.58769ZM21.7173 2.85589C21.559 2.78684 21.3993 2.72057 21.238 2.65714L20.5057 4.51825C20.6443 4.57278 20.7817 4.62974 20.9178 4.6891L21.7173 2.85589ZM29.3345 10.7536C29.2707 10.5913 29.2039 10.4303 29.1343 10.2709L27.3013 11.071C27.3611 11.208 27.4185 11.3464 27.4734 11.4859L29.3345 10.7536ZM29.1363 21.7239C29.2052 21.566 29.2713 21.4066 29.3345 21.2458L27.4734 20.5135C27.4191 20.6518 27.3622 20.7888 27.3031 20.9245L29.1363 21.7239ZM21.238 29.3424C21.3966 29.28 21.5538 29.2148 21.7095 29.147L20.9111 27.3133C20.7772 27.3716 20.642 27.4276 20.5057 27.4812L21.238 29.3424ZM10.267 29.1438C10.4252 29.2128 10.5848 29.279 10.7458 29.3424L11.4781 27.4812C11.3397 27.4268 11.2025 27.3699 11.0665 27.3106L10.267 29.1438ZM2.64932 21.2458C2.71265 21.4068 2.77879 21.5663 2.84771 21.7243L4.68097 20.9248C4.62173 20.789 4.56487 20.6519 4.51043 20.5135L2.64932 21.2458ZM2.84976 10.2705C2.78011 10.43 2.71328 10.5911 2.64933 10.7537L4.51044 11.486C4.56541 11.3462 4.62286 11.2078 4.68274 11.0706L2.84976 10.2705ZM10.7458 2.65713C10.5821 2.72155 10.4199 2.78889 10.2592 2.85908L11.0598 4.69184C11.198 4.6315 11.3374 4.57362 11.4781 4.51825L10.7458 2.65713ZM8.68067 8.69138C9.08208 8.28997 9.35844 7.80831 9.56915 7.36684C9.77357 6.93853 9.95979 6.44789 10.1135 6.07756C10.4726 5.21238 10.734 4.83418 11.0598 4.69184L10.2592 2.85908C9.1063 3.36271 8.59522 4.51839 8.2663 5.31085C8.07599 5.76935 7.93931 6.13847 7.7642 6.50536C7.59537 6.85908 7.43557 7.10805 7.26645 7.27716L8.68067 8.69138ZM4.68274 11.0706C4.82493 10.7449 5.20289 10.4834 6.06772 10.1242C6.43787 9.97049 6.9284 9.78419 7.35654 9.57976C7.79786 9.36903 8.27936 9.09268 8.68067 8.69138L7.26645 7.27716C7.09734 7.44627 6.8484 7.60608 6.49476 7.77495C6.12793 7.9501 5.75895 8.08682 5.30056 8.27721C4.5083 8.60628 3.35302 9.11758 2.84976 10.2705L4.68274 11.0706ZM5.6586 15.9997C5.6586 15.4314 5.51303 14.8948 5.34945 14.4332C5.19076 13.9854 4.97495 13.5062 4.82145 13.1353C4.4627 12.2685 4.38039 11.8165 4.51044 11.486L2.64933 10.7537C2.18796 11.9262 2.64501 13.1065 2.97347 13.9001C3.16358 14.3594 3.32834 14.7175 3.46431 15.1012C3.59541 15.4712 3.6586 15.7605 3.6586 15.9997H5.6586ZM4.51043 20.5135C4.38038 20.183 4.46269 19.731 4.82144 18.8642C4.97495 18.4933 5.19076 18.0141 5.34945 17.5663C5.51303 17.1047 5.6586 16.5681 5.6586 15.9997H3.6586C3.6586 16.239 3.59541 16.5283 3.46431 16.8983C3.32834 17.282 3.16358 17.64 2.97347 18.0993C2.64501 18.893 2.18796 20.0733 2.64932 21.2458L4.51043 20.5135ZM8.68617 23.3052C8.28432 22.9034 7.80193 22.6268 7.35981 22.4161C6.93084 22.2116 6.43941 22.0253 6.06842 21.8716C5.20153 21.5123 4.82308 21.2507 4.68097 20.9248L2.84771 21.7243C3.35123 22.879 4.50909 23.3902 5.30263 23.7191C5.76186 23.9095 6.13167 24.0462 6.49915 24.2214C6.85349 24.3903 7.10276 24.5502 7.27196 24.7194L8.68617 23.3052ZM11.0665 27.3106C10.7407 27.1685 10.4791 26.79 10.1198 25.923C9.96605 25.552 9.77983 25.0606 9.57533 24.6316C9.36457 24.1895 9.08804 23.7071 8.68617 23.3052L7.27196 24.7194C7.44115 24.8886 7.60105 25.1379 7.76997 25.4922C7.94516 25.8597 8.08189 26.2296 8.27222 26.6888C8.60111 27.4824 9.11237 28.6403 10.267 29.1438L11.0665 27.3106ZM23.2922 23.3055C22.8901 23.7076 22.6135 24.1903 22.4027 24.6328C22.1981 25.0621 22.0119 25.5539 21.858 25.9252C21.4985 26.793 21.2369 27.1714 20.9111 27.3133L21.7095 29.147C22.8652 28.6438 23.3768 27.4848 23.7057 26.6907C23.8962 26.2311 24.0329 25.8609 24.2082 25.4931C24.3772 25.1384 24.5372 24.889 24.7064 24.7197L23.2922 23.3055ZM27.3031 20.9245C27.1609 21.2505 26.782 21.5121 25.9135 21.8714C25.5419 22.0252 25.0498 22.2113 24.6202 22.4158C24.1775 22.6266 23.6945 22.9032 23.2922 23.3055L24.7064 24.7197C24.8757 24.5505 25.1251 24.3905 25.48 24.2216C25.8479 24.0464 26.2183 23.9097 26.6782 23.7195C27.4726 23.3907 28.6324 22.8797 29.1363 21.7239L27.3031 20.9245ZM26.3253 15.9997C26.3253 16.5681 26.4708 17.1047 26.6344 17.5663C26.7931 18.0141 27.0089 18.4933 27.1624 18.8642C27.5212 19.731 27.6035 20.183 27.4734 20.5135L29.3345 21.2458C29.7959 20.0733 29.3389 18.893 29.0104 18.0994C28.8203 17.64 28.6555 17.282 28.5196 16.8983C28.3885 16.5283 28.3253 16.239 28.3253 15.9997H26.3253ZM23.2977 8.69108C23.6994 9.0928 24.1816 9.36927 24.6235 9.58001C25.0523 9.78448 25.5435 9.97068 25.9143 10.1244C26.7806 10.4836 27.1591 10.7451 27.3013 11.071L29.1343 10.2709C28.6306 9.1169 27.4734 8.60578 26.6802 8.27691C26.2213 8.08661 25.8517 7.94991 25.4844 7.77477C25.1302 7.60589 24.8811 7.44604 24.7119 7.27687L23.2977 8.69108ZM15.9919 26.3331C15.4236 26.3331 14.887 26.4787 14.4254 26.6422C13.9775 26.8009 13.4984 27.0167 13.1275 27.1702C12.2607 27.529 11.8087 27.6113 11.4781 27.4812L10.7458 29.3424C11.9184 29.8037 13.0987 29.3467 13.8923 29.0182C14.3516 28.8281 14.7097 28.6633 15.0934 28.5274C15.4633 28.3963 15.7526 28.3331 15.9919 28.3331V26.3331ZM20.9178 4.6891C21.2435 4.8312 21.5051 5.2094 21.8644 6.0754C22.0181 6.44603 22.2044 6.93708 22.4089 7.36569C22.6196 7.80746 22.8961 8.28947 23.2977 8.69108L24.7119 7.27687C24.5428 7.10771 24.3829 6.85857 24.214 6.50452C24.0388 6.1373 23.9021 5.76784 23.7117 5.30897C23.3827 4.51597 22.8713 3.35921 21.7173 2.85589L20.9178 4.6891ZM15.9919 5.66641C16.5603 5.66641 17.0969 5.52084 17.5585 5.35727C18.0063 5.19857 18.4855 4.98276 18.8564 4.82926C19.7232 4.47051 20.1752 4.3882 20.5057 4.51825L21.238 2.65714C20.0655 2.19577 18.8852 2.65282 18.0915 2.98129C17.6322 3.17139 17.2742 3.33615 16.8905 3.47212C16.5205 3.60322 16.2312 3.66641 15.9919 3.66641V5.66641ZM27.4734 11.4859C27.6035 11.8165 27.5212 12.2685 27.1624 13.1353C27.0089 13.5062 26.7931 13.9854 26.6344 14.4332C26.4708 14.8948 26.3253 15.4314 26.3253 15.9997H28.3253C28.3253 15.7605 28.3885 15.4712 28.5196 15.1012C28.6555 14.7175 28.8203 14.3594 29.0104 13.9001C29.3389 13.1065 29.7959 11.9262 29.3345 10.7536L27.4734 11.4859ZM20.5057 27.4812C20.1752 27.6113 19.7232 27.529 18.8564 27.1702C18.4855 27.0167 18.0063 26.8009 17.5585 26.6422C17.0969 26.4787 16.5603 26.3331 15.9919 26.3331V28.3331C16.2312 28.3331 16.5205 28.3963 16.8905 28.5274C17.2742 28.6633 17.6322 28.8281 18.0915 29.0182C18.8852 29.3467 20.0655 29.8037 21.238 29.3424L20.5057 27.4812ZM15.9919 3.66641C15.7526 3.66641 15.4633 3.60322 15.0934 3.47212C14.7097 3.33615 14.3516 3.17139 13.8923 2.98128C13.0987 2.65282 11.9184 2.19577 10.7458 2.65713L11.4781 4.51825C11.8087 4.3882 12.2607 4.47051 13.1275 4.82926C13.4984 4.98276 13.9775 5.19857 14.4254 5.35727C14.887 5.52084 15.4236 5.66641 15.9919 5.66641V3.66641Z"
      fill="currentColor"
    />
    <circle
      cx="13.0547"
      cy="13.0648"
      r="1.05538"
      transform="rotate(45 13.0547 13.0648)"
      fill="currentColor"
    />
    <circle
      cx="18.9297"
      cy="18.9359"
      r="1.05538"
      transform="rotate(45 18.9297 18.9359)"
      fill="currentColor"
    />
    <path
      d="M12.9986 18.9858L18.9688 13.0156"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StampIcon = ({
  disabled,
  color
}: {
  disabled: boolean;
  color?: string;
}) => (
  <svg
    className={disabled ? 'text-[#C4C4C4]' : color ? '' : 'text-primary'}
    style={!disabled && color ? { color: color } : undefined}
    width="28"
    height="28"
    viewBox="0 0 28 28"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M16.9141 12.832H23.3307V20.9987C23.3307 23.576 21.2414 25.6654 18.6641 25.6654H9.33073C6.7534 25.6654 4.66406 23.576 4.66406 20.9987V12.832H11.0807"
      stroke="currentColor"
      strokeWidth="1.75"
    />
    <path
      d="M19.25 12.8314H22.75C23.7165 12.8314 24.5 12.0479 24.5 11.0814V8.74805C24.5 7.78155 23.7165 6.99805 22.75 6.99805H5.25C4.2835 6.99805 3.5 7.78155 3.5 8.74805V11.0814C3.5 12.0479 4.2835 12.8314 5.25 12.8314H8.75"
      stroke="currentColor"
      strokeWidth="1.75"
    />
    <path
      d="M14 5.2487C14 3.63787 15.3058 2.33203 16.9167 2.33203H17.5C18.7887 2.33203 19.8333 3.3767 19.8333 4.66536V4.66536C19.8333 5.95403 18.7887 6.9987 17.5 6.9987H14V5.2487Z"
      stroke="currentColor"
      strokeWidth="1.75"
    />
    <path
      d="M14 5.2487C14 3.63787 12.6942 2.33203 11.0833 2.33203H10.5C9.21134 2.33203 8.16667 3.3767 8.16667 4.66536V4.66536C8.16667 5.95403 9.21134 6.9987 10.5 6.9987H14V5.2487Z"
      stroke="currentColor"
      strokeWidth="1.75"
    />
    <path
      d="M16.9141 6.99805L16.9141 25.6647L11.0807 25.6647L11.0807 6.99805L16.9141 6.99805Z"
      stroke="currentColor"
      strokeWidth="1.75"
    />
  </svg>
);

const PointIcon = ({
  disabled,
  color
}: {
  disabled: boolean;
  color?: string;
}) => (
  <svg
    className={disabled ? 'text-[#C4C4C4]' : color ? '' : 'text-primary'}
    style={!disabled && color ? { color: color } : undefined}
    width="29"
    height="29"
    viewBox="0 0 29 29"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2.80281 11.6421L2.50741 12.4989L2.80281 11.6421ZM2.80281 17.3553L2.50741 16.4985L2.80281 17.3553ZM26.2076 17.3553L26.503 16.4985L26.2076 17.3553ZM26.2076 11.6421L25.9122 10.7854L26.2076 11.6421ZM7.25521 3.92578C4.08532 3.92578 1.51562 6.49548 1.51562 9.66537H3.32812C3.32812 7.4965 5.08634 5.73828 7.25521 5.73828V3.92578ZM21.7552 3.92578H7.25521V5.73828H21.7552V3.92578ZM27.4948 9.66536C27.4948 6.49548 24.9251 3.92578 21.7552 3.92578V5.73828C23.9241 5.73828 25.6823 7.4965 25.6823 9.66536H27.4948ZM27.4948 11.1432V9.66536H25.6823V11.1432H27.4948ZM25.0781 14.4987C25.0781 13.5737 25.6726 12.7852 26.503 12.4989L25.9122 10.7854C24.3733 11.316 23.2656 12.7768 23.2656 14.4987H25.0781ZM26.503 16.4985C25.6726 16.2122 25.0781 15.4237 25.0781 14.4987H23.2656C23.2656 16.2206 24.3733 17.6814 25.9122 18.212L26.503 16.4985ZM27.4948 19.332V17.8542H25.6823V19.332H27.4948ZM21.7552 25.0716C24.9251 25.0716 27.4948 22.5019 27.4948 19.332H25.6823C25.6823 21.5009 23.9241 23.2591 21.7552 23.2591V25.0716ZM7.25521 25.0716H21.7552V23.2591H7.25521V25.0716ZM1.51562 19.332C1.51562 22.5019 4.08532 25.0716 7.25521 25.0716V23.2591C5.08634 23.2591 3.32812 21.5009 3.32812 19.332H1.51562ZM1.51562 17.8542V19.332H3.32812V17.8542H1.51562ZM3.09821 18.212C4.63716 17.6814 5.74479 16.2206 5.74479 14.4987H3.93229C3.93229 15.4237 3.33782 16.2122 2.50741 16.4985L3.09821 18.212ZM5.74479 14.4987C5.74479 12.7768 4.63716 11.316 3.09821 10.7854L2.50741 12.4989C3.33782 12.7852 3.93229 13.5737 3.93229 14.4987H5.74479ZM1.51562 9.66537V11.1432H3.32812V9.66537H1.51562ZM3.09821 10.7854C3.17893 10.8132 3.32812 10.9196 3.32812 11.1432H1.51562C1.51562 11.8266 1.99205 12.3212 2.50741 12.4989L3.09821 10.7854ZM3.32812 17.8542C3.32812 18.0778 3.17893 18.1842 3.09821 18.212L2.50741 16.4985C1.99205 16.6762 1.51562 17.1708 1.51562 17.8542H3.32812ZM25.9122 18.212C25.8315 18.1842 25.6823 18.0778 25.6823 17.8542H27.4948C27.4948 17.1708 27.0184 16.6762 26.503 16.4985L25.9122 18.212ZM25.6823 11.1432C25.6823 10.9196 25.8315 10.8132 25.9122 10.7854L26.503 12.4989C27.0184 12.3212 27.4948 11.8266 27.4948 11.1432H25.6823Z"
      fill="currentColor"
    />
    <path
      d="M14.0231 10.3044C14.2648 9.98432 14.7456 9.98432 14.9873 10.3044L16.1841 11.8891C16.2564 11.9848 16.3556 12.0568 16.4689 12.096L18.3459 12.7445C18.725 12.8755 18.8735 13.3328 18.6438 13.6616L17.5066 15.2895C17.4379 15.3878 17.4 15.5044 17.3978 15.6243L17.361 17.6098C17.3536 18.0108 16.9646 18.2934 16.5809 18.1765L14.6812 17.598C14.5665 17.563 14.4439 17.563 14.3292 17.598L12.4295 18.1765C12.0458 18.2934 11.6569 18.0108 11.6494 17.6098L11.6126 15.6243C11.6104 15.5044 11.5726 15.3878 11.5039 15.2895L10.3666 13.6616C10.1369 13.3328 10.2854 12.8755 10.6645 12.7445L12.5415 12.096C12.6549 12.0568 12.754 11.9848 12.8263 11.8891L14.0231 10.3044Z"
      stroke="currentColor"
      strokeWidth="1.8125"
    />
  </svg>
);

// ── TnC sub-components ────────────────────────────────────────────────────────

const InStoreSteps: FC = () => (
  <div className="flex flex-col gap-0.5">
    <h3 className="text-xs text-primary">How to Redeem</h3>
    <div className="flex flex-col gap-3 pt-1">
      <div className="flex items-start gap-3">
        <span className="mt-1 flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">
          1
        </span>
        <div>
          <p className="text-sm">Visit our store</p>
          <p className="text-xs text-gray-500">
            Find your nearest participating location
          </p>
        </div>
      </div>
      <div className="flex items-start gap-3">
        <span className="mt-1 flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">
          2
        </span>
        <div>
          <p className="text-sm">Enjoy reward</p>
          <p className="text-xs text-gray-500">
            At checkout, you&apos;ll see the reward available &mdash; simply
            apply it manually.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const EcomSteps: FC<{ merchantName: string }> = ({ merchantName }) => (
  <div className="flex flex-col gap-0.5">
    <h3 className="text-xs text-primary">How to Redeem</h3>
    <div className="flex flex-col gap-3 pt-1">
      <div className="flex items-start gap-3">
        <span className="mt-1 flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">
          1
        </span>
        <div>
          <p className="text-sm">Copy the Unique Code</p>
          <p className="text-xs text-gray-500">
            Copy the unique code provided below.
          </p>
        </div>
      </div>
      <div className="flex items-start gap-3">
        <span className="mt-1 flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">
          2
        </span>
        <div>
          <p className="text-sm">Visit {merchantName} Online</p>
          <p className="text-xs text-gray-500">
            Enter the code at checkout page to redeem your reward.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const RewardCodeBox: FC<{
  couponCode: string;
  copied: boolean;
  onCopy: () => void;
}> = ({ couponCode, copied, onCopy }) => (
  <div className="flex flex-col gap-2">
    <h3 className="text-xs text-primary">Reward Code</h3>
    <div className="rounded-xl border border-gray-200 p-4">
      <p className="mb-2 text-center text-xs text-gray-400">
        Copy and paste this code at online checkout
      </p>
      <div className="flex items-center justify-between rounded-sm bg-orange-700/10 px-4 py-3">
        <p className="text-md font-mono font-bold tracking-widest text-gray-900">
          {couponCode}
        </p>
        <Button
          variant="ghost"
          className="flex items-center gap-1 text-sm font-semibold text-primary"
          onClick={onCopy}
          type="button"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="8"
              y="8"
              width="12"
              height="12"
              rx="2"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              d="M16 8V6C16 4.89543 15.1046 4 14 4H6C4.89543 4 4 4.89543 4 6V14C4 15.1046 4.89543 16 6 16H8"
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
          {copied ? 'COPIED!' : 'COPY'}
        </Button>
      </div>
    </div>
  </div>
);

// ──────────────────────────────────────────────────────────────────────────────

const getIcons = (
  disabled: boolean,
  color?: string
): Record<CurrencyTypeWithDiscount, ReactNode> => {
  return {
    [CurrencyTypeWithDiscount.CASHBACK]: (
      <CashbackIcon disabled={disabled} color={color} />
    ),
    [CurrencyTypeWithDiscount.DISCOUNT]: (
      <DiscountIcon disabled={disabled} color={color} />
    ),
    [CurrencyTypeWithDiscount.STAMPS]: (
      <StampIcon disabled={disabled} color={color} />
    ),
    [CurrencyTypeWithDiscount.POINTS]: (
      <PointIcon disabled={disabled} color={color} />
    )
  };
};

export const RewardCard: FC<RewardCardProps> = ({
  merchantName = '',
  color = '',
  title = 'Discount 10%',
  type,
  subtitle = '',
  validFrom = '',
  validUntil = '20/07/2024',
  multiplier = 1,
  moreDetails = null,
  rewardString = '',
  applicable_entities = [],
  applicable_items = [],
  showTnc = true,
  availableMRP,
  ...reward
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [copied, setCopied] = useState(false);
  const formatToLocalTime = (isoTime: string, format: string): string => {
    if (!isoTime) return '';
    const date = new Date(isoTime);
    if (isNaN(date.getTime())) return '';

    // Convert to dayjs from the local Date object for formatting
    return dayjs(date).format(format);
  };

  const rewardChannel: RewardChannel = reward.rewardChannel || 'in_store';
  const couponCode = reward.couponCode || null;
  const showInStore = rewardChannel === 'in_store' || rewardChannel === 'both';

  const tncInfo = [
    {
      title: 'Reward Period',
      description: `${validFrom ? formatToLocalTime(validFrom, 'DD/MM/YYYY HH:mm') : ''} - ${validUntil ? formatToLocalTime(validUntil, 'DD/MM/YYYY HH:mm') : ''}`
    },
    {
      title: 'Reward',
      description: rewardString
    },
    ...(applicable_items?.length > 1
      ? [
          {
            title: 'Eligible Purchases',
            description: applicable_items.join(', ')
          }
        ]
      : []),
    ...(moreDetails
      ? [
          {
            title: 'More Details',
            description: moreDetails
          }
        ]
      : [])
  ];

  const handleCopyCoupon = () => {
    if (!couponCode) return;
    navigator.clipboard.writeText(couponCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  let disabled = false;
  if (reward.status === 'past') {
    disabled = true;
  } else if (
    reward.requiredMRPToRedeem &&
    (availableMRP || 0) < reward.requiredMRPToRedeem
  ) {
    disabled = true;
  }

  return (
    <>
      <div className="inner-curve relative overflow-hidden">
        {process.env.REACT_APP_FEATURE_REWARD_NAME_VISIBLE === 'true' && (
          <span className="text-xs italic text-gray-400">
            {reward.rewardCode}
          </span>
        )}
        <div className="custom-dash flex justify-between divide-x-3">
          <div
            className={cn(
              'flex flex-grow items-center gap-4 rounded-md p-4 py-2',
              disabled ? 'bg-[#E5E5E54A]' : color ? '' : 'bg-primary/10'
            )}
            style={
              !disabled && color
                ? { backgroundColor: `${color}1A` } // Adding 1A for ~10% opacity
                : undefined
            }
          >
            <div className="flex size-12 shrink-0 items-center justify-center rounded-full">
              {getIcons(disabled, color)[type]}
            </div>
            <div className="flex w-full flex-col space-y-1">
              <div className="flex w-full justify-between">
                {/* Title */}
                <h3
                  className={cn(
                    'text-md font-medium text-gray-900',
                    disabled && 'text-[#00000080]'
                  )}
                >
                  {title}
                </h3>
                {/* Merchant name at top right */}
                <p
                  className={cn(
                    'text-xs',
                    disabled ? 'text-[#979797]' : color ? '' : 'text-primary'
                  )}
                  style={!disabled && color ? { color: color } : undefined}
                >
                  {merchantName}
                </p>
              </div>
              <div className="flex flex-col">
                {/* Sub-title */}
                <p className={cn('text-sm', disabled && 'text-[#959595]')}>
                  {subtitle}
                </p>
                {/* Progress bar for stamp collection */}
                {type === CurrencyTypeWithDiscount.STAMPS &&
                  reward.requiredMRPToRedeem && (
                    <div className="mt-0.5 flex flex-row items-center gap-x-2">
                      <progress
                        className="mt-1 h-1 w-4/5 rounded-full sm:w-3/5 [&::-moz-progress-bar]:bg-primary [&::-webkit-progress-bar]:bg-[#E5E5E5] [&::-webkit-progress-value]:bg-primary"
                        value={availableMRP}
                        max={reward.requiredMRPToRedeem}
                        style={{
                          backgroundColor: '#E5E5E5',
                          color: color || 'var(--primary-color)' // Use color prop or fallback
                        }}
                      />
                      <span
                        className={cn('text-sm', disabled && 'text-[#959595]')}
                      >
                        {availableMRP}/{reward.requiredMRPToRedeem}
                      </span>
                    </div>
                  )}
                {/* Validity & T&C */}
                <div className="mt-0.5 flex gap-4 text-xs">
                  <p className="text-gray-400">
                    Valid Until:{' '}
                    {validUntil
                      ? formatToLocalTime(validUntil, 'DD/MM/YYYY')
                      : '-'}
                  </p>
                  {showTnc && (
                    <Button
                      variant="link"
                      className="h-4 cursor-pointer p-0 text-[#005ABE]"
                      onClick={() => {
                        setOpenDialog(true);
                      }}
                      type="button"
                    >
                      T&C
                    </Button>
                  )}
                </div>
                {/* Available/Earned points to collect this voucher */}
                {type === CurrencyTypeWithDiscount.POINTS &&
                  reward.requiredMRPToRedeem && (
                    <div className="mt-0.5 flex flex-row items-center gap-1">
                      <p className="text-sm italic text-gray-500">
                        Available{' '}
                        {reward.isGlobal ? '' : reward.entityName + ' '}{' '}
                        {reward.MPN}: {availableMRP || 0}/
                        {reward.requiredMRPToRedeem}
                      </p>
                    </div>
                  )}
              </div>
            </div>
          </div>
          <div
            className={cn(
              'relative mr-1 w-9 rounded-md',
              disabled ? 'bg-[#E5E5E54A]' : color ? '' : 'bg-primary/10'
            )}
            style={
              !disabled && color
                ? { backgroundColor: `${color}1A` } // Adding 1A for ~10% opacity
                : undefined
            }
          >
            {multiplier > 1 && (
              <div className="absolute -right-1 top-1 flex flex-col items-end">
                <div
                  className={cn(
                    'rounded-l-full bg-[#FFD6C9] p-1 pl-2',
                    disabled ? 'bg-[#E5E5E5]' : 'bg-[#FFD6C9]'
                  )}
                >
                  <p
                    className={cn(
                      'flex justify-center text-sm font-semibold',
                      disabled ? 'text-[#979797]' : 'text-primary'
                    )}
                  >
                    x{multiplier}
                  </p>
                </div>
                <div
                  className={cn(
                    'h-0 w-0 rotate-[-90deg] border-l-5 border-t-5 border-transparent',
                    disabled ? 'border-t-[#E5E5E5]' : 'border-t-[#FFD6C9]'
                  )}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="flex max-h-[85vh] max-w-[24rem] flex-col overflow-hidden rounded-3xl p-0">
          <div className="flex-shrink-0 px-6 pt-6">
            <DialogHeader className="flex flex-col items-start">
              T&amp;C
            </DialogHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 pb-6 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1">
            {/* Channel Pill */}
            <div className="my-2 flex justify-center">
              <span className="rounded-full bg-primary px-4 py-1.5 text-xs font-semibold tracking-wider text-white">
                {REWARD_CHANNEL_LABELS[rewardChannel]}
              </span>
            </div>

            <RewardCard
              merchantName={merchantName}
              type={type}
              title={title}
              subtitle={subtitle}
              validUntil={validUntil}
              multiplier={multiplier}
              availableMRP={availableMRP}
              validFrom={validFrom}
              {...reward}
              showTnc={false}
            />

            {/* T&C info sections */}
            <div className="mt-3 flex flex-col gap-8 py-2">
              {tncInfo.map((info, index) => (
                <div key={index} className="flex flex-col gap-0.5">
                  <h3 className="text-xs text-primary">{info.title}</h3>
                  <p className="text-sm">{info.description}</p>
                </div>
              ))}

              {/* === HOW TO REDEEM === */}
              {rewardChannel === 'both' ? (
                <>
                  {/* REDEEM IN-STORE section */}
                  <div className="flex flex-col gap-4">
                    <h3 className="text-sm font-bold uppercase tracking-wide text-gray-900">
                      Redeem In-Store
                    </h3>
                    <InStoreSteps />
                    {applicable_entities?.length >= 1 && (
                      <div className="flex flex-col gap-0.5">
                        <h3 className="text-xs text-primary">
                          Applicable Store
                        </h3>
                        <p className="text-sm">
                          {applicable_entities.join(', ')}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* REDEEM ONLINE section */}
                  <div className="flex flex-col gap-4">
                    <h3 className="text-sm font-bold uppercase tracking-wide text-gray-900">
                      Redeem Online
                    </h3>
                    <EcomSteps merchantName={merchantName} />
                  </div>

                  {couponCode && (
                    <RewardCodeBox
                      couponCode={couponCode}
                      copied={copied}
                      onCopy={handleCopyCoupon}
                    />
                  )}
                </>
              ) : showInStore ? (
                <>
                  {/* IN-STORE How to Redeem */}
                  <InStoreSteps />
                  {applicable_entities?.length >= 1 && (
                    <div className="flex flex-col gap-0.5">
                      <h3 className="text-xs text-primary">Applicable Store</h3>
                      <p className="text-sm">
                        {applicable_entities.join(', ')}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* E-COMMERCE How to Redeem */}
                  <EcomSteps merchantName={merchantName} />
                  {couponCode && (
                    <RewardCodeBox
                      couponCode={couponCode}
                      copied={copied}
                      onCopy={handleCopyCoupon}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RewardCard;
